import {
  ToggleGroup as ShadToggleGroup,
  ToggleGroupItem as ShadToggleGroupItem,
} from "@/components/ui/toggle-group"
import "./index.scss"
import { cn } from "@/lib/utils"
import { MouseEvent, useEffect, useState } from "react"
import { Icon } from "@/icons/icon"
import { ErrorFlagState } from "@/components/ui/input"
import { TinyText } from "@/components/tiny-text"

export interface ToggleGroupOption {
  label: string
  value: string
}

export interface ToggleGroupProps {
  className?: string
  options: ToggleGroupOption[]
  value?: string
  correctValue?: string
  disabled?: boolean
  flagState?: ErrorFlagState
  helpText?: string
  onChange?: (value: string) => void
  onClickRedFlag?: () => void
}

export default function ToggleGroup({
  className,
  options,
  value,
  correctValue,
  disabled,
  flagState = ErrorFlagState.None,
  helpText,
  onChange,
  onClickRedFlag,
}: ToggleGroupProps) {
  const [localValue, setLocalValue] = useState("")

  function handleValueChange(value: string) {
    setLocalValue(value || "")

    if (onChange) {
      onChange(value || "")
    }
  }

  function handleClickErrorFlag(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault()
    e.stopPropagation()

    if (onClickRedFlag) {
      onClickRedFlag()
    }
  }

  useEffect(() => {
    setLocalValue(value || "")
  }, [value])

  return (
    <div className={cn(className)}>
      <ShadToggleGroup
        className={cn(
          `
          bg-white border border-input-border rounded-sm
          choice-reading relative w-full
        `,
          disabled && "opacity-60 hover:cursor-not-allowed pointer-events-none"
        )}
        type="single"
        value={localValue}
        onValueChange={handleValueChange}
      >
        {options.map((option) => (
          <ShadToggleGroupItem
            key={option.value}
            asChild
            className={cn(
              `
              px-5 py-3.5 not-last:border-r border-r-input-border
              active-indicator relative h-12 text-label-text
              hover:cursor-pointer bg-white
            `,
              disabled && "hover:cursor-not-allowed"
            )}
            value={option.value}
          >
            <div className="flex items-center justify-between w-full gap-2">
              <span className="truncate" title={option.label}>{option.label}</span>

              {!!localValue &&
                localValue !== correctValue &&
                option.value === localValue && (
                  <button
                    className="hover:cursor-pointer"
                    type="button"
                    onClick={handleClickErrorFlag}
                  >
                    {flagState === ErrorFlagState.Filled && (
                      <Icon name="RedFlag" className="size-4" />
                    )}

                    {flagState === ErrorFlagState.Unfilled && (
                      <Icon name="FlagNeutral" className="size-4" />
                    )}
                  </button>
                )}

              {!!localValue &&
                localValue === correctValue &&
                option.value === localValue && (
                  <Icon name="Checkbox" className="size-4" />
                )}
            </div>
          </ShadToggleGroupItem>
        ))}
      </ShadToggleGroup>

      {!!helpText && <TinyText text={helpText} />}
    </div>
  )
}
